import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Grid,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
} from '@mui/material';
import TablePeo from '../../Components/TablePeo';
import SearchIcon from '@mui/icons-material/Search';
import { useApiGet } from '../../utils/httpClient';
import { getMessageList, getMessageStatus } from '../../endpoints/resources';

const headCells = [
  {
    id: 'from_name',
    label: 'From',
    width: '40%',
    firstCell: true,
  },
  {
    id: 'to_name',
    label: 'To',
    width: '40%',
    firstCell: true,
  },
  {
    id: 'status',
    label: 'Status',
    width: '10%',
  },
];

export default function MessageList() {
  let [searchParams, setSearchParams] = useSearchParams();

  const updateSearchParams = useCallback(
    (key, value) => {
      if (!value || value === '') {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const [order, setOrder] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [status, setStatus] = useState(
    searchParams.get('status') || '750f426bc6824e60a2773917cbf5419f'
  );
  const [delaySearch, setDelaySearch] = useState(
    searchParams.get('search') || ''
  );

  // need up update search params when search changes
  useEffect(() => {
    updateSearchParams('search', search);
  }, [search, updateSearchParams]);
  // need up update params when status changes
  useEffect(() => {
    updateSearchParams('status', status);
  }, [status, updateSearchParams]);

  const { data, isFetching, refetch } = useApiGet(
    ['message-list', rowsPerPage, page, orderBy, order, search, status],
    getMessageList,
    {
      enabled: true,
      retry: 1,
    },
    {
      rowsPerPage,
      page,
      orderBy,
      order,
      search,
      status,
    }
  );

  const { data: statusList } = useApiGet(['message-status'], getMessageStatus, {
    enabled: true,
    retry: 1,
  });

  // need to refresh the query when the path is hit again
  useEffect(() => {
    if (searchParams.get('refresh') === 'true') {
      refetch();
    }
  }, [searchParams, refetch]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(delaySearch);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delaySearch]);

  const handleClick = (event, id) => {
    window.location.href = `/message/${id}`;
  };

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12} paddingBottom={5} textAlign={'center'}>
        <Typography variant="h4" color={'primary'}>
          Message List
        </Typography>
      </Grid>
      <Grid container>
        <Grid item xs={12} sm={6} paddingRight={2} paddingBottom={3}>
          <TextField
            label="Search"
            id="search-field"
            size="small"
            value={delaySearch}
            fullWidth={true}
            onChange={(event) => setDelaySearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth={true} variant="outlined" size="small">
            <InputLabel id="disbanded">Status</InputLabel>
            <Select
              labelId="select-label"
              id="select"
              fullWidth={true}
              value={status}
              input={<OutlinedInput label="Status" />}
              label="Status"
              onChange={(event) => setStatus(event.target.value)}
            >
              <MenuItem key={'all'} value={'all'}>
                {'All'}
              </MenuItem>
              {statusList &&
                statusList.map((st) => (
                  <MenuItem key={st.id} value={st.id}>
                    {st.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <TablePeo
        data={data}
        isFetching={isFetching}
        headCells={headCells}
        handleRowClick={handleClick}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        order={order}
        setOrder={setOrder}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
      />
    </Grid>
  );
}
